import { GameInventoryItem } from "@shared/types";
import { css, htmlArray } from "../helpers/webComponents";
import { InventoryRouteService } from "../services/InventoryRouteService";

/** CSS affecting the {@link InventoryComponent} */
const styles: string = css`
    .content {
        color: white;
        z-index: 99999;
        position: absolute;
        left: 50%;
        top: 20%;
        -webkit-transform: translate(-50%, -50%);
        transform: translate(-50%, -50%);
        background-color: rgba(0, 0, 0, 0.8);
        padding: 0.75em;
    }

    .hidden {
        display: none;
    }

    .content ul {
        padding: 1em;
    }

    button {
        background-color: red;
        border-color: red;
        color: white;
        font-size: 1em;
        cursor: pointer;
        padding: 0;
        margin-left: 0.5em;
    }
`;

/**
 * Represents the Canvas page
 */
export class InventoryComponent extends HTMLElement {
    /** Instance of the game route service */
    private readonly _inventoryRouteService: InventoryRouteService = new InventoryRouteService();

    private inventory: GameInventoryItem[] = [];

    /**
     * The "constructor" of a Web Component
     */
    public async connectedCallback(): Promise<void> {
        this.attachShadow({ mode: "open" });

        window.addEventListener("toggle-component", e => {
            if (e instanceof CustomEvent && e.detail === "inventory") {
                this.toggle();
            }
        }, false);
        this.handleRemoveItem();
        await this.refreshInventory();
        this.render();
    }

    /**
     * Refresh the current inventory
     */
    private async refreshInventory(): Promise<void> {
        this.inventory = await this._inventoryRouteService.getInventory();
    }

    private toggle(): void {
        const content: HTMLElement | null | undefined = this.shadowRoot?.querySelector(".content");
        if (content) {
            content.classList.toggle("hidden");
        }
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderContent()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        return `
            <div class="content hidden">
            <center>
                <h2>Inventory</h2>
            </center>
            <ul>
                ${this.inventory.length > 0
                ? this.inventory.map((item, index) => `
                    <li>
                        ${item.reference.name} (x${item.quantity})
                        <button class="remove-item" data-index="${index}">X</button>
                    </li>
                `).join("")
                : "<li>Empty</li>"}
            </ul>
            </div>
        `;
    }

    /**
     * Handle events for the remove-item button
     */
    private handleRemoveItem(): void {
        this.shadowRoot?.addEventListener("click", async event => {
            const target: HTMLElement = event.target as HTMLElement;
            if (target.classList.contains("remove-item")) {
                const index: string | null = target.getAttribute("data-index");
                if (index !== null) {
                    const itemIndex: number = parseInt(index, 10);
                    const itemToRemove: GameInventoryItem = this.inventory[itemIndex];
                    await this._inventoryRouteService.removeItemFromInventory(itemToRemove.reference.alias);
                    await this.refreshInventory();
                    this.render();
                }
            }
        });
    }
}
